/// <amd-module name="TFS/Admin/Groups/Pivots/Groups/Redux/GroupsPivotReducers" />
import produce from "immer";
import * as Constants from "TFS/Admin/Common/Constants/Constants";
import { numberOfGroupsPerBatch, GroupOperation, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { IGroupMembers, IPermissionsGroupsState, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions, GroupsPivotActionTypes } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { caseInsensitiveContains, EmptyGuidString, equals } from "VSS/Core/Util/String";

export function permissionsGroupsReducer(state: IPermissionsGroupsState, action: GroupsPivotActions): IPermissionsGroupsState {
    return produce(state || {}, draft => {
        switch (action.type) {
            case GroupsPivotActionTypes.OpenAddGroupPanel: {
                draft.selectedItem = action.payload;
                draft.showAddGroupPanel = true;
                draft.groupNameAlreadyExists = false;
                break;
            }

            case GroupsPivotActionTypes.CloseAddGroupPanel: {
                draft.showAddGroupPanel = false;
                break;
            }

            case GroupsPivotActionTypes.DismissMessageCard: {
                draft.showMessageCard = false;

                // Reset
                draft.groupOperationResultMessage = "";
                break;
            }

            case GroupsPivotActionTypes.DisposeView: {
                // @ts-ignore TypeScript 4.0 upgrade
                delete draft.data;
                break;
            }

            case GroupsPivotActionTypes.InitView: {
                draft.searchResult = {
                    keyword: "",
                    resultItems: [],
                    status: SearchStatus.None,
                    hasMore: false
                };
                draft.initComplete = false;
                draft.showMessageCard = false;
                draft.multiOperationStatuses = [];
                draft.supportServicePrincipals = false;
                break;
            }

            case GroupsPivotActionTypes.DismissMultiSummary: {
                draft.multiOperationStatuses = [];
                break;
            }

            case GroupsPivotActionTypes.GroupOperationStatus: {
                const { operation, resultMessage, result, severity, group, multiOperationStatuses } = action.payload;

                if (multiOperationStatuses) {
                    draft.multiOperationStatuses = multiOperationStatuses;
                }
                if (operation == GroupOperation.Add && group) {
                    insertNewGroup(draft, group);
                }
                draft.showMessageCard = true;
                draft.groupOperationResultMessage = resultMessage;
                draft.groupOperationSuccess = result;
                draft.groupOperationSeverity = severity;
                break;
            }

            case GroupsPivotActionTypes.GroupsLoadFailed: {
                draft.data = {
                    totalGroupCount: 0,
                    groups: [],
                    errorMessage: "Unable to load groups and users.",
                    moreData: false,
                    numberOfGroupsOnScreen: 0
                };
                draft.loading = false;
                draft.initComplete = true;
                break;
            }

            case GroupsPivotActionTypes.GroupsLoadSucceeded: {
                const pagedGroups = action.payload;

                draft.data = {
                    totalGroupCount: pagedGroups.identities.length,
                    groups: updatePermissionGroups(pagedGroups.identities),
                    errorMessage: "",
                    moreData: pagedGroups.moreData,
                    continuationToken: undefined,
                    numberOfGroupsOnScreen: numberOfGroupsPerBatch
                };
                draft.loading = false;
                draft.initComplete = true;
                draft.isProjectScope = pagedGroups.isProjectScope;
                break;
            }

            case GroupsPivotActionTypes.UpdateNumberOfGroupsOnScreen: {
                const numberOfGroupsOnScreen = action.payload;

                draft.data.numberOfGroupsOnScreen = numberOfGroupsOnScreen;
                break;
            }

            case GroupsPivotActionTypes.UpdateMemberCountAndLinkProps: {
                const { groupMembers, filtered } = action.payload;
                updateMembersCountAndLinkProps(filtered ? draft.searchResult.resultItems : draft.data.groups, groupMembers);
                break;
            }

            case GroupsPivotActionTypes.UpdateMembers: {
                const { groupMembers, filtered } = action.payload;
                updateMembers(filtered ? draft.searchResult.resultItems : draft.data.groups, groupMembers);
                break;
            }

            case GroupsPivotActionTypes.SearchGroups: {
                updateSearchStatus(draft, SearchStatus.Searching);
                break;
            }

            case GroupsPivotActionTypes.UpdateSearchStatus: {
                updateSearchStatus(draft, action.payload);
                break;
            }

            case GroupsPivotActionTypes.UpdateSearchResult: {
                updateSearchResult(draft, action.payload.keyword, action.payload.result, action.payload.hasMore);
                break;
            }

            case GroupsPivotActionTypes.FetchOrgSettingsCompleted: {
                const settingsData = action.payload;
                let isAadOrg = false;
                if (settingsData.orgnizationTenantData.id) {
                    isAadOrg = settingsData.orgnizationTenantData.id != EmptyGuidString;
                }
                draft.isAadOrg = isAadOrg;
                draft.supportServicePrincipals = settingsData.supportServicePrincipals === true;
                break;
            }

            case GroupsPivotActionTypes.SetMarkTimeToInteract: {
                draft.toMarkTimeToInteract = true;
                break;
            }

            case GroupsPivotActionTypes.UpdateDefaultTeamFlag: {
                const { teamId } = action.payload;

                clearDefaultTeamFlag(draft.data.groups);
                setDefaultTeamFlag(draft.data.groups, teamId);

                if (draft.searchResult && draft.searchResult.resultItems.length > 0) {
                    clearDefaultTeamFlag(draft.searchResult.resultItems);
                    setDefaultTeamFlag(draft.searchResult.resultItems, teamId);
                }

                break;
            }

            case GroupsPivotActionTypes.FetchPermissionsGroupsSuccess: {
                const { identities } = action.payload;

                draft.permissionsGroups = identities;
                draft.showAddGroupPanel = true;
                draft.groupNameAlreadyExists = false;
                break;
            }

            case GroupsPivotActionTypes.CheckGroupExistence: {
                draft.groupNameAlreadyExists = checkIfGroupAlreadyExists(action.payload, draft);
                break;
            }

            case GroupsPivotActionTypes.IsAddGroupDisabled: {
                draft.isAddGroupDisabled = action.payload;
                break;
            }

            default:
                return state;
        }
    });
}

export function setDefaultTeamFlag(data: PermissionsGroup[], originId: string) {
    const itemIndex = data.findIndex(x => x.originId == originId);
    if (itemIndex > -1) {
        data[itemIndex].isDefaultTeam = true;
    }
}

export function clearDefaultTeamFlag(data: PermissionsGroup[]) {
    const itemIndex = data.findIndex(x => x.isDefaultTeam == true);
    if (itemIndex > -1) {
        data[itemIndex].isDefaultTeam = false;
    }
}

export function updatePermissionGroups(data: PermissionsGroup[]): PermissionsGroup[] {
    const groups: PermissionsGroup[] = [];

    for (const graphGroup of data) {
        groups.push({
            ...(graphGroup as PermissionsGroup),
            isAadGroup: graphGroup.origin !== undefined && caseInsensitiveContains(graphGroup.origin, Constants.aadPrefix),
            updateImage: false,
            isDefaultTeam: graphGroup.isDefaultTeam
        });
    }

    return groups;
}

function getSortedIndex(array: any[], value: any, isLessThan: (a: any, b: any) => boolean): number {
    let low = 0;
    let high = array.length;
    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (isLessThan(array[mid], value)) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }
    return low;
}

function insertNewGroup(state: IPermissionsGroupsState, groupModel: PermissionsGroup) {
    const permissionsGroup: PermissionsGroup = { ...(groupModel as PermissionsGroup), isAadGroup: false, updateImage: false, isDefaultTeam: false };
    const isNameLessThan = (a: any, b: any) => a.displayName < b.displayName;
    const isLessThan = !state.isProjectScope
        ? isNameLessThan
        : (a: any, b: any) => a.subjectKind < b.subjectKind || (a.subjectKind == b.subjectKind && isNameLessThan(a, b));
    const { groups } = state.data;
    groups.splice(getSortedIndex(groups, groupModel, isLessThan), 0, permissionsGroup);
    if (state.searchResult.status !== SearchStatus.None && caseInsensitiveContains(groupModel.displayName, state.searchResult.keyword)) {
        const { resultItems } = state.searchResult;
        resultItems.splice(getSortedIndex(resultItems, groupModel, isNameLessThan), 0, permissionsGroup);
    }
}

function updateMembersCountAndLinkProps(data: PermissionsGroup[], groupMember: IGroupMembers) {
    const itemIndex = data.findIndex(x => x.descriptor == groupMember.groupDescriptor);

    if (itemIndex > -1) {
        data[itemIndex].membersCount = groupMember.totalMemberCount;
        data[itemIndex].members = [];
        data[itemIndex].linkProps = groupMember.linkProps;
    }
}

function updateMembers(data: PermissionsGroup[], groupMember: IGroupMembers) {
    const itemIndex = data.findIndex(x => x.descriptor == groupMember.groupDescriptor);
    if (itemIndex > -1) {
        data[itemIndex].members = groupMember.members;
    }
}

function updateSearchStatus(state: IPermissionsGroupsState, searchStatus: SearchStatus) {
    const { searchResult } = state;
    if (!searchResult) {
        return;
    }
    if (searchStatus === SearchStatus.None) {
        searchResult.resultItems = [];
        searchResult.keyword = "";
    }
    searchResult.status = searchStatus;
}

function updateSearchResult(state: IPermissionsGroupsState, keyword: string, results: any[], hasMore: boolean) {
    if (!keyword || !state.searchResult) {
        return;
    }
    state.searchResult = {
        keyword,
        resultItems: results,
        status: SearchStatus.Completed,
        hasMore
    };
}

function checkIfGroupAlreadyExists(groupName: string, state: IPermissionsGroupsState): boolean {
    const { groups } = state.data;

    if (groups && groups.length > 0) {
        const itemIndex = groups.findIndex(x => equals(x.displayName, groupName, true));
        if (itemIndex > -1) {
            return true;
        }
    }

    return false;
}
